import type { Creator, CreatorGroup } from "@/shared/types/campaign/creator";

export interface CreatorGroupSummary {
	sumOfFinalRate: {
		primary?: string;
		converted?: string;
	};
	hiddenCreatorsCount: number;
	totalCreators: number;
	totalFollowers?: string;
}

type CreatorVisibilityMap = Record<string, boolean>;

const formatFollowers = (value: number): string => {
	if (value >= 1_000_000) {
		return `${Math.floor(value / 1_000_000)}M`;
	}

	if (value >= 1_000) {
		return `${Math.floor(value / 1_000)}K`;
	}

	return value.toString();
};

const getCreatorFollowersTotal = (creator: Creator): number => {
	let total = 0;

	if (creator.tiktok?.followers) total += Number(creator.tiktok.followers) || 0;
	if (creator.instagram?.followers) total += Number(creator.instagram.followers) || 0;
	if (creator.facebook?.followers) total += Number(creator.facebook.followers) || 0;
	if (creator.youtube?.subscribers) total += Number(creator.youtube.subscribers) || 0;

	return total;
};

const getCreatorVisibleState = (creator: Creator, visibilityMap?: CreatorVisibilityMap): boolean => {
	if (visibilityMap && Object.prototype.hasOwnProperty.call(visibilityMap, creator.id)) {
		return !!visibilityMap[creator.id];
	}

	return creator.assignment?.clientVisible === true;
};

export const calculateCreatorGroupSummary = (
	group: CreatorGroup,
	isClientOnly: boolean,
	visibilityMap?: CreatorVisibilityMap,
): CreatorGroupSummary => {
	const visibleCreators = group.creators.filter((creator) => getCreatorVisibleState(creator, visibilityMap));
	const rateCreators = isClientOnly ? visibleCreators : group.creators;

	const sumPrimary = rateCreators.reduce((total, creator) => {
		const rateValue = (creator.finalRate ?? creator.assignment?.finalRate) as number | string | null | undefined;
		if (rateValue === null || rateValue === undefined || rateValue === "") {
			return total;
		}

		const numericRate = typeof rateValue === "number" ? rateValue : Number(String(rateValue).replace(/,/g, ""));
		return Number.isNaN(numericRate) ? total : total + numericRate;
	}, 0);

	const sumConverted = rateCreators.reduce((total, creator) => {
		const rateValue = (creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate) as
			| number
			| string
			| null
			| undefined;
		if (rateValue === null || rateValue === undefined || rateValue === "") {
			return total;
		}

		const numericRate = typeof rateValue === "number" ? rateValue : Number(String(rateValue).replace(/,/g, ""));
		return Number.isNaN(numericRate) ? total : total + numericRate;
	}, 0);

	const totalFollowers = group.creators.reduce((total, creator) => total + getCreatorFollowersTotal(creator), 0);

	return {
		sumOfFinalRate: {
			primary: sumPrimary > 0 ? sumPrimary.toFixed(2) : undefined,
			converted: sumConverted > 0 ? sumConverted.toFixed(2) : undefined,
		},
		hiddenCreatorsCount: group.creators.length - visibleCreators.length,
		totalCreators: group.creators.length,
		totalFollowers: totalFollowers > 0 ? formatFollowers(totalFollowers) : undefined,
	};
};

export const applyCreatorGroupSummary = (
	group: CreatorGroup,
	isClientOnly: boolean,
	visibilityMap?: CreatorVisibilityMap,
): CreatorGroup => ({
	...group,
	...calculateCreatorGroupSummary(group, isClientOnly, visibilityMap),
});
